export type UpgradePool = 'cookie' | 'toggle' | 'prestige' | 'tech' | 'debug' | '';

export interface UpgradeDef {
  id: string;
  name: string;
  desc: string;
  price: number;
  icon: string;
  pool: UpgradePool;
  tier: number;
  order: number;
  /** Building this upgrade applies to, if any. */
  buildingId?: string;
  /** CpS multiplier power (e.g., 1 = +1%, 2 = +2%). */
  power?: number;
  /** Custom unlock condition. If omitted, unlocked by tier thresholds. */
  unlockCondition?: string;
}

function tieredUpgrade(
  buildingId: string,
  tier: number,
  name: string,
  desc: string,
  price: number,
  icon: string,
): UpgradeDef {
  return {
    id: `${buildingId}_t${tier}`,
    name,
    desc,
    price,
    icon,
    pool: 'cookie',
    tier,
    order: tier * 100,
    buildingId,
    power: 2,
  };
}

function cookieUpgrade(
  id: string,
  name: string,
  desc: string,
  price: number,
  power: number,
  icon: string,
  order: number = 0,
): UpgradeDef {
  return { id, name, desc, price, icon, pool: 'cookie', tier: 0, order, power };
}

export const UPGRADES: UpgradeDef[] = [
  // === Cookie flavors (global CpS multipliers) ===
  cookieUpgrade('plain_cookies', 'Plain cookies', 'Cookie production multiplier +1%.', 999_999, 1, '🍪', 10000),
  cookieUpgrade('sugar_cookies', 'Sugar cookies', 'Cookie production multiplier +1%.', 5_000_000, 1, '🍪', 10001),
  cookieUpgrade('oatmeal_raisin', 'Oatmeal raisin cookies', 'Cookie production multiplier +1%.', 10_000_000, 1, '🍪', 10002),
  cookieUpgrade('peanut_butter', 'Peanut butter cookies', 'Cookie production multiplier +1%.', 50_000_000, 1, '🍪', 10003),
  cookieUpgrade('coconut_cookies', 'Coconut cookies', 'Cookie production multiplier +2%.', 100_000_000, 2, '🍪', 10004),
  cookieUpgrade('white_chocolate', 'White chocolate cookies', 'Cookie production multiplier +2%.', 500_000_000, 2, '🍪', 10005),
  cookieUpgrade('macadamia', 'Macadamia nut cookies', 'Cookie production multiplier +2%.', 1e9, 2, '🍪', 10006),
  cookieUpgrade('double_chip', 'Double-chip cookies', 'Cookie production multiplier +2%.', 5e9, 2, '🍪', 10007),
  cookieUpgrade('white_choc_macadamia', 'White chocolate macadamia cookies', 'Cookie production multiplier +2%.', 1e10, 2, '🍪', 10008),
  cookieUpgrade('all_chocolate', 'All-chocolate cookies', 'Cookie production multiplier +2%.', 5e10, 2, '🍪', 10009),
  cookieUpgrade('dark_chocolate', 'Dark chocolate-coated cookies', 'Cookie production multiplier +4%.', 1e11, 4, '🍪', 10010),
  cookieUpgrade('white_chocolate_coated', 'White chocolate-coated cookies', 'Cookie production multiplier +4%.', 5e11, 4, '🍪', 10011),
  cookieUpgrade('eclipse_cookies', 'Eclipse cookies', 'Cookie production multiplier +5%.', 1e12, 5, '🍪', 10012),
  cookieUpgrade('zebra_cookies', 'Zebra cookies', 'Cookie production multiplier +5%.', 5e12, 5, '🍪', 10013),
  cookieUpgrade('snickerdoodles', 'Snickerdoodles', 'Cookie production multiplier +5%.', 1e13, 5, '🍪', 10014),
  cookieUpgrade('stroopwafels', 'Stroopwafels', 'Cookie production multiplier +5%.', 5e13, 5, '🍪', 10015),
  cookieUpgrade('macaroons', 'Macaroons', 'Cookie production multiplier +5%.', 1e14, 5, '🍪', 10016),

  // === Cursor upgrades ===
  tieredUpgrade('cursor', 1, 'Reinforced index finger', 'The mouse and cursors are twice as efficient.', 100, '👆'),
  tieredUpgrade('cursor', 2, 'Carpal tunnel prevention cream', 'The mouse and cursors are twice as efficient.', 500, '👆'),
  tieredUpgrade('cursor', 3, 'Ambidextrous', 'The mouse and cursors are twice as efficient.', 10_000, '👆'),
  tieredUpgrade('cursor', 4, 'Thousand fingers', 'The mouse and cursors gain +0.1 cookies for each non-cursor building.', 100_000, '👆'),
  tieredUpgrade('cursor', 5, 'Million fingers', 'The mouse and cursors gain +0.5 cookies for each non-cursor building.', 10_000_000, '👆'),
  tieredUpgrade('cursor', 6, 'Billion fingers', 'The mouse and cursors gain +5 cookies for each non-cursor building.', 100_000_000, '👆'),
  tieredUpgrade('cursor', 7, 'Trillion fingers', 'The mouse and cursors gain +50 cookies for each non-cursor building.', 1e9, '👆'),

  // === Grandma upgrades ===
  tieredUpgrade('grandma', 1, 'Forwards from grandma', 'Grandmas are twice as efficient.', 1_000, '👵'),
  tieredUpgrade('grandma', 2, 'Steel-plated rolling pins', 'Grandmas are twice as efficient.', 5_000, '👵'),
  tieredUpgrade('grandma', 3, 'Lubricated dentures', 'Grandmas are twice as efficient.', 50_000, '👵'),
  tieredUpgrade('grandma', 4, 'Prune juice', 'Grandmas are twice as efficient.', 5_000_000, '👵'),
  tieredUpgrade('grandma', 5, 'Double-thick glasses', 'Grandmas are twice as efficient.', 500_000_000, '👵'),

  // === Farm upgrades ===
  tieredUpgrade('farm', 1, 'Cheap hoes', 'Farms are twice as efficient.', 11_000, '🌾'),
  tieredUpgrade('farm', 2, 'Fertilizer', 'Farms are twice as efficient.', 55_000, '🌾'),
  tieredUpgrade('farm', 3, 'Cookie trees', 'Farms are twice as efficient.', 550_000, '🌾'),
  tieredUpgrade('farm', 4, 'Genetically-modified cookies', 'Farms are twice as efficient.', 55_000_000, '🌾'),
  tieredUpgrade('farm', 5, 'Gingerbread scarecrows', 'Farms are twice as efficient.', 5_500_000_000, '🌾'),

  // === Mine upgrades ===
  tieredUpgrade('mine', 1, 'Sugar gas', 'Mines are twice as efficient.', 120_000, '⛏️'),
  tieredUpgrade('mine', 2, 'Megadrill', 'Mines are twice as efficient.', 600_000, '⛏️'),
  tieredUpgrade('mine', 3, 'Ultradrill', 'Mines are twice as efficient.', 6_000_000, '⛏️'),
  tieredUpgrade('mine', 4, 'Ultimadrill', 'Mines are twice as efficient.', 600_000_000, '⛏️'),
  tieredUpgrade('mine', 5, 'H-bomb mining', 'Mines are twice as efficient.', 60_000_000_000, '⛏️'),

  // === Factory upgrades ===
  tieredUpgrade('factory', 1, 'Sturdier conveyor belts', 'Factories are twice as efficient.', 1_300_000, '🏭'),
  tieredUpgrade('factory', 2, 'Child labor', 'Factories are twice as efficient.', 6_500_000, '🏭'),
  tieredUpgrade('factory', 3, 'Sweatshop', 'Factories are twice as efficient.', 65_000_000, '🏭'),
  tieredUpgrade('factory', 4, 'Radium reactors', 'Factories are twice as efficient.', 6_500_000_000, '🏭'),
  tieredUpgrade('factory', 5, 'Recombobulators', 'Factories are twice as efficient.', 650_000_000_000, '🏭'),

  // === Bank upgrades ===
  tieredUpgrade('bank', 1, 'Taller tellers', 'Banks are twice as efficient.', 14_000_000, '🏦'),
  tieredUpgrade('bank', 2, 'Scissor-resistant credit cards', 'Banks are twice as efficient.', 70_000_000, '🏦'),
  tieredUpgrade('bank', 3, 'Acid-proof vaults', 'Banks are twice as efficient.', 700_000_000, '🏦'),
  tieredUpgrade('bank', 4, 'Chocolate coins', 'Banks are twice as efficient.', 70_000_000_000, '🏦'),

  // === Temple upgrades ===
  tieredUpgrade('temple', 1, 'Golden idols', 'Temples are twice as efficient.', 200_000_000, '🛕'),
  tieredUpgrade('temple', 2, 'Sacrifices', 'Temples are twice as efficient.', 1_000_000_000, '🛕'),
  tieredUpgrade('temple', 3, 'Delicious blessing', 'Temples are twice as efficient.', 10_000_000_000, '🛕'),

  // === Wizard tower upgrades ===
  tieredUpgrade('wizard_tower', 1, 'Pointier hats', 'Wizard towers are twice as efficient.', 3_300_000_000, '🧙'),
  tieredUpgrade('wizard_tower', 2, 'Beardlier beards', 'Wizard towers are twice as efficient.', 16_500_000_000, '🧙'),
  tieredUpgrade('wizard_tower', 3, 'Ancient grimoires', 'Wizard towers are twice as efficient.', 165_000_000_000, '🧙'),

  // === Shipment upgrades ===
  tieredUpgrade('shipment', 1, 'Vanilla nebulae', 'Shipments are twice as efficient.', 51_000_000_000, '🚀'),
  tieredUpgrade('shipment', 2, 'Wormholes', 'Shipments are twice as efficient.', 255_000_000_000, '🚀'),
  tieredUpgrade('shipment', 3, 'Frequent flyer', 'Shipments are twice as efficient.', 2_550_000_000_000, '🚀'),

  // === Alchemy lab upgrades ===
  tieredUpgrade('alchemy_lab', 1, 'Antimony', 'Alchemy labs are twice as efficient.', 750_000_000_000, '⚗️'),
  tieredUpgrade('alchemy_lab', 2, 'Essence of dough', 'Alchemy labs are twice as efficient.', 3_750_000_000_000, '⚗️'),
  tieredUpgrade('alchemy_lab', 3, 'True chocolate', 'Alchemy labs are twice as efficient.', 37_500_000_000_000, '⚗️'),

  // === Portal upgrades ===
  tieredUpgrade('portal', 1, 'Ancient tablet', 'Portals are twice as efficient.', 10_000_000_000_000, '🌀'),
  tieredUpgrade('portal', 2, 'Insane oatling workers', 'Portals are twice as efficient.', 50_000_000_000_000, '🌀'),
  tieredUpgrade('portal', 3, 'Soul bond', 'Portals are twice as efficient.', 500_000_000_000_000, '🌀'),

  // === Time machine upgrades ===
  tieredUpgrade('time_machine', 1, 'Flux capacitors', 'Time machines are twice as efficient.', 140_000_000_000_000, '⏰'),
  tieredUpgrade('time_machine', 2, 'Time paradox resolver', 'Time machines are twice as efficient.', 700_000_000_000_000, '⏰'),

  // === Antimatter condenser upgrades ===
  tieredUpgrade('antimatter_condenser', 1, 'Sugar bosons', 'Antimatter condensers are twice as efficient.', 1_700_000_000_000_000, '⚛️'),
  tieredUpgrade('antimatter_condenser', 2, 'String theory', 'Antimatter condensers are twice as efficient.', 8_500_000_000_000_000, '⚛️'),

  // === Prism upgrades ===
  tieredUpgrade('prism', 1, 'Gem polish', 'Prisms are twice as efficient.', 21_000_000_000_000_000, '🔺'),
  tieredUpgrade('prism', 2, '9th color', 'Prisms are twice as efficient.', 105_000_000_000_000_000, '🔺'),

  // === Chancemaker upgrades ===
  tieredUpgrade('chancemaker', 1, 'Your lucky cookie', 'Chancemakers are twice as efficient.', 260_000_000_000_000_000, '🎰'),
  tieredUpgrade('chancemaker', 2, 'All-seeing eye', 'Chancemakers are twice as efficient.', 1.3e18, '🎰'),

  // === Fractal engine upgrades ===
  tieredUpgrade('fractal_engine', 1, 'Recursive mirrors', 'Fractal engines are twice as efficient.', 3.1e18, '🔮'),

  // === Clicking upgrades ===
  {
    id: 'plastic_mouse',
    name: 'Plastic mouse',
    desc: 'Clicking gains +1% of your CpS.',
    price: 50_000,
    icon: '🖱️',
    pool: 'cookie',
    tier: 0,
    order: 20000,
    power: 1,
    unlockCondition: 'clicks:1000',
  },
  {
    id: 'iron_mouse',
    name: 'Iron mouse',
    desc: 'Clicking gains +1% of your CpS.',
    price: 5_000_000,
    icon: '🖱️',
    pool: 'cookie',
    tier: 0,
    order: 20001,
    power: 1,
    unlockCondition: 'clicks:100000',
  },
  {
    id: 'titanium_mouse',
    name: 'Titanium mouse',
    desc: 'Clicking gains +1% of your CpS.',
    price: 500_000_000,
    icon: '🖱️',
    pool: 'cookie',
    tier: 0,
    order: 20002,
    power: 1,
    unlockCondition: 'clicks:10000000',
  },
];
